import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { TriangleAlert } from "lucide-react";

const VISIBLE_MS = 2 * 60 * 1000; // stays for 2 minutes
const WAIT_MS = 10 * 60 * 1000; // reappears after 10 minutes

// "Gone for good" applies to the current page load only — a fresh
// load/refresh starts a new cycle so the disclaimer is always seen.
let doneThisLoad = false;

/**
 * Emergency disclaimer banner — home page only.
 * Lifecycle: visible (2 min or until scroll) → hidden (10 min) → visible again
 * (2 min or until scroll) → gone for good (until the page is reloaded).
 */
export const EmergencyBanner = () => {
  const { pathname } = useLocation();
  const [phase, setPhase] = useState(() => (doneThisLoad ? "done" : "visible1"));

  const finish = useCallback(() => {
    doneThisLoad = true;
    setPhase("done");
  }, []);

  useEffect(() => {
    let t;
    if (phase === "visible1") t = setTimeout(() => setPhase("waiting"), VISIBLE_MS);
    else if (phase === "waiting") t = setTimeout(() => setPhase("visible2"), WAIT_MS);
    else if (phase === "visible2") t = setTimeout(finish, VISIBLE_MS);
    return () => clearTimeout(t);
  }, [phase, finish]);

  useEffect(() => {
    if (phase !== "visible1" && phase !== "visible2") return;
    const onScroll = () => {
      if (window.scrollY > 40) {
        if (phase === "visible1") setPhase("waiting");
        else finish();
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [phase, finish]);

  if (pathname !== "/" || (phase !== "visible1" && phase !== "visible2")) return null;

  return (
    <div
      data-testid="emergency-banner"
      className="sticky top-0 z-[60] bg-red-50 border-b border-red-200 px-4 py-1.5 text-center"
    >
      <p className="text-xs sm:text-sm text-red-800 leading-snug flex items-center justify-center gap-1.5 flex-wrap">
        <TriangleAlert className="w-3.5 h-3.5 shrink-0" />
        <span>
          <b>NOT FOR EMERGENCIES.</b> If you are experiencing a medical emergency, please visit the
          nearest hospital immediately or call <b>112</b>.
        </span>
      </p>
    </div>
  );
};
